/* Store locator for the Stockists page (works like stockist.co):
   search a city/postcode or use the visitor's location, move the map there and list stores within the radius by distance.
   Map: Leaflet + OpenStreetMap tiles. Place search: OpenStreetMap Nominatim for names, Photon (komoot) for postcodes; one request per search. */
if (!customElements.get('store-locator')) {
  customElements.define(
    'store-locator',
    class StoreLocator extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('[data-stockist]'));
        this.list = this.querySelector('[data-list]');
        this.status = this.querySelector('[data-count]');
        this.form = this.querySelector('[data-search-form]');
        this.input = this.querySelector('[data-search]');
        this.nearButton = this.querySelector('[data-near]');
        this.radius = parseFloat(this.dataset.radius) || 100;
        this.markers = new Map();
        this.requestId = 0;

        // Before a search, list every store (if any) without distances
        this.items.forEach((item) => { item.hidden = false; });

        this.initMap();

        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.searchPlace(this.input.value.trim());
        });
        this.nearButton.addEventListener('click', () => this.locate());
        this.items.forEach((item) => {
          item.querySelector('[data-focus]').addEventListener('click', () => this.focus(item, true));
        });
      }

      initMap() {
        const el = this.querySelector('[data-map]');
        if (!window.L || !el) return;

        this.map = L.map(el, { scrollWheelZoom: false });
        // OpenStreetMap tiles (free, no key; made black and white in CSS)
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(this.map);

        this.items.forEach((item) => {
          const lat = parseFloat(item.dataset.lat);
          const lng = parseFloat(item.dataset.lng);
          if (Number.isNaN(lat) || Number.isNaN(lng)) return;
          const marker = L.circleMarker([lat, lng], this.markerStyle(false)).addTo(this.map);
          marker.bindPopup(this.popupHtml(item), { closeButton: false, offset: [0, -4] });
          marker.on('click', () => this.focus(item, false));
          this.markers.set(item, marker);
        });

        this.showOverview();
        // Leaflet measures its container on init; re-measure once layout has settled
        setTimeout(() => { this.map.invalidateSize(); this.showOverview(); }, 200);
      }

      showOverview() {
        if (this.searched) return;
        const markers = Array.from(this.markers.values());
        if (markers.length) {
          this.map.fitBounds(L.featureGroup(markers).getBounds(), { padding: [40, 40], maxZoom: 12 });
        } else {
          this.map.setView([50, 10], 4); // Europe
        }
      }

      markerStyle(active) {
        return { radius: active ? 9 : 6, color: '#000', weight: active ? 2 : 1, fillColor: active ? '#fff' : '#000', fillOpacity: 1 };
      }

      popupHtml(item) {
        const name = item.querySelector('.stockists__name').textContent;
        const directions = item.querySelector('.stockists__links a').getAttribute('href');
        return `<strong class="stockists__popup-name">${name}</strong><a class="stockists__popup-link" href="${directions}" target="_blank" rel="noopener">DIRECTIONS</a>`;
      }

      setStatus(text) {
        this.status.textContent = text;
      }

      async searchPlace(query) {
        if (!query) return;
        // only the latest search may update the page (a slow earlier answer must not overwrite a newer one)
        const request = ++this.requestId;
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        this.setStatus('SEARCHING…');
        try {
          const place = await this.geocode(query, this.controller.signal);
          if (request !== this.requestId) return;
          if (!place) {
            this.setStatus(this.dataset.notFound);
            return;
          }
          const { lat, lng, bounds } = place;
          this.showResults([lat, lng], bounds);
        } catch (error) {
          if (error.name === 'AbortError' || request !== this.requestId) return;
          this.setStatus(this.dataset.notFound);
        }
      }

      // Returns { lat, lng, bounds: [south, north, west, east] | null } or null.
      // Queries with a number (postcodes, house numbers) use Photon, which handles postcodes like "1012 NX" well;
      // plain names use OpenStreetMap Nominatim, which answers much faster.
      async geocode(query, signal) {
        const lang = document.documentElement.lang || 'en';
        if (/\d/.test(query)) {
          const url = `https://photon.komoot.io/api/?limit=1&lang=en&lat=50&lon=10&zoom=4&location_bias_scale=0.2&q=${encodeURIComponent(query)}`;
          const response = await fetch(url, { headers: { Accept: 'application/json' }, signal });
          const data = response.ok ? await response.json() : { features: [] };
          const place = data.features && data.features[0];
          if (!place) return null;
          const [lng, lat] = place.geometry.coordinates;
          const e = place.properties.extent; // [west, north, east, south]
          return { lat, lng, bounds: e ? [e[3], e[1], e[0], e[2]] : null };
        }
        const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&accept-language=${lang}&q=${encodeURIComponent(query)}`;
        const response = await fetch(url, { headers: { Accept: 'application/json' }, signal });
        const results = response.ok ? await response.json() : [];
        const place = results[0];
        if (!place) return null;
        return { lat: parseFloat(place.lat), lng: parseFloat(place.lon), bounds: place.boundingbox ? place.boundingbox.map(parseFloat) : null };
      }

      locate() {
        if (!navigator.geolocation) return;
        // a location request replaces any search still in progress
        this.requestId++;
        if (this.controller) this.controller.abort();
        const label = this.nearButton.textContent;
        this.nearButton.textContent = 'LOCATING…';
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            this.nearButton.textContent = label;
            this.input.value = '';
            this.showResults([pos.coords.latitude, pos.coords.longitude], null);
          },
          () => { this.nearButton.textContent = label; },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
        );
      }

      showResults(here, bounds) {
        this.searched = true;
        const nearby = [];
        this.items.forEach((item) => {
          const lat = parseFloat(item.dataset.lat);
          const lng = parseFloat(item.dataset.lng);
          const km = Number.isNaN(lat) ? Infinity : this.distance(here, [lat, lng]);
          item.dataset.km = km;
          const inRange = km <= this.radius;
          item.hidden = !inRange;
          const out = item.querySelector('[data-distance]');
          out.hidden = !inRange;
          out.textContent = km < 10 ? `${km.toFixed(1)} KM` : `${Math.round(km)} KM`;
          if (inRange) nearby.push(item);
        });
        nearby.sort((a, b) => a.dataset.km - b.dataset.km).forEach((item) => this.list.appendChild(item));

        this.setStatus(nearby.length
          ? `${nearby.length} ${nearby.length === 1 ? 'STOCKIST' : 'STOCKISTS'} NEARBY`
          : this.dataset.noResults);

        if (!this.map) return;
        if (this.here) this.here.remove();
        this.here = L.circleMarker(here, { radius: 5, color: '#000', weight: 2, fillColor: '#fff', fillOpacity: 1 }).addTo(this.map);
        if (nearby.length) {
          const points = [here, ...nearby.map((item) => this.markers.get(item).getLatLng())];
          this.map.fitBounds(L.latLngBounds(points), { padding: [60, 60], maxZoom: 13 });
        } else if (bounds) {
          this.map.fitBounds([[bounds[0], bounds[2]], [bounds[1], bounds[3]]], { maxZoom: 12 });
        } else {
          this.map.setView(here, 11);
        }
      }

      focus(item, fromList) {
        this.items.forEach((i) => i.classList.toggle('is-active', i === item));
        this.markers.forEach((marker, i) => marker.setStyle(this.markerStyle(i === item)));
        const marker = this.markers.get(item);
        if (marker && this.map) {
          if (fromList) this.map.flyTo(marker.getLatLng(), Math.max(this.map.getZoom(), 13), { duration: 0.6 });
          marker.openPopup();
          marker.bringToFront();
        }
        if (!fromList) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }

      distance(a, b) {
        const rad = (d) => (d * Math.PI) / 180;
        const dLat = rad(b[0] - a[0]);
        const dLng = rad(b[1] - a[1]);
        const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(dLng / 2) ** 2;
        return 2 * 6371 * Math.asin(Math.sqrt(h));
      }
    }
  );
}
